/**
 * VoteButton Component
 * 
 * Displays upvote/downvote buttons with vote counts
 * Supports real-time vote updates
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from '@/components/ui/Text';
import { castVote, getUserVote, subscribeToItemVotes, type VoteType, type VoteAggregate } from '@/services/voting';
import { useUserStore } from '@/state/userStore';
import { useToast } from '@/components/ui/ToastProvider';
import { isUiDebugLoggingEnabled, logger } from '@/services/logger';

type VoteButtonProps = {
  tripId: string;
  itemId: string;
  itemType: 'itinerary_item' | 'suggestion' | 'place' | 'activity';
  size?: 'small' | 'medium' | 'large';
  showCounts?: boolean;
  orientation?: 'horizontal' | 'vertical';
  onVoteChange?: (aggregate: VoteAggregate) => void;
};

export const VoteButton: React.FC<VoteButtonProps> = ({
  tripId,
  itemId,
  itemType,
  size = 'medium',
  showCounts = true,
  orientation = 'horizontal',
  onVoteChange,
}) => {
  const theme = useTheme();
  const { firebaseUser } = useUserStore();
  const { showError } = useToast();
  const [userVote, setUserVote] = useState<VoteType | null>(null);
  const [aggregate, setAggregate] = useState<VoteAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [, forceUpdate] = useState(0);

  const userId = firebaseUser?.uid;
  const onVoteChangeRef = useRef(onVoteChange);
  onVoteChangeRef.current = onVoteChange;

  // Load initial vote and aggregate
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadVotes = async () => {
      try {
        const vote = await getUserVote(tripId, itemId, userId);

        if (vote) {
          setUserVote(vote.vote === 'neutral' ? null : vote.vote);
        }
      } catch (error) {
        logger.error('[VoteButton] Error loading votes', error instanceof Error ? error : new Error(String(error)));
      } finally {
        setLoading(false);
      }
    };

    loadVotes();
  }, [tripId, itemId, userId]);

  // Subscribe to real-time vote updates. onVoteChange is read via ref so the effect doesn't
  // re-run when the parent passes a new callback (avoids re-subscribing every render).
  useEffect(() => {
    if (!tripId || !itemId) return;

    const unsubscribe = subscribeToItemVotes(
      tripId,
      itemId,
      itemType,
      (agg) => {
        setAggregate(agg);
        if (onVoteChangeRef.current && agg) {
          onVoteChangeRef.current(agg);
        }
        requestAnimationFrame(() => forceUpdate((n) => n + 1));
      }
    );

    return () => unsubscribe();
  }, [tripId, itemId, itemType]);

  const handleVote = async (vote: VoteType) => {
    // Debug logging for development
    if (__DEV__ || isUiDebugLoggingEnabled()) {
      console.log('[VoteButton] handleVote called', { vote, itemType, voting });
    }
    
    // Guard: user must be logged in
    if (!userId) {
    if (__DEV__ || isUiDebugLoggingEnabled()) {
      console.log('[VoteButton] No authenticated user, cannot vote');
    }
      showError('Please sign in to vote');
      return;
    }
    
    // Guard: prevent voting on optimistic (not-yet-saved) items
    if (itemId.startsWith('__optimistic_')) {
    if (__DEV__ || isUiDebugLoggingEnabled()) {
      console.log('[VoteButton] Cannot vote on optimistic item, waiting for save');
    }
      showError('Please wait - item is still saving');
      return;
    }
    
    // Guard: prevent double-voting while a vote is in progress
    if (voting) {
    if (__DEV__ || isUiDebugLoggingEnabled()) {
      console.log('[VoteButton] Vote already in progress, ignoring');
    }
      return;
    }

    // If clicking the same vote, remove it (toggle off)
    const finalVote = userVote === vote ? 'neutral' : vote;
    
    if (__DEV__ || isUiDebugLoggingEnabled()) {
      console.log('[VoteButton] Casting vote', { finalVote, previousVote: userVote });
    }

    setVoting(true);
    try {
      const result = await castVote(tripId, itemId, itemType, finalVote, userId);
      
      if (__DEV__ || isUiDebugLoggingEnabled()) {
        console.log('[VoteButton] castVote result', result);
      }
      
      if (result.success) {
        // Update local state immediately for better UX
        setUserVote(finalVote === 'neutral' ? null : finalVote);
        // Nudge re-render so UI updates without waiting for subscription / app switch
        requestAnimationFrame(() => forceUpdate((n) => n + 1));
      } else {
        logger.warn('[VoteButton] Failed to cast vote', { error: result.error });
        showError(result.error || "Couldn't register vote. Try again.");
      }
    } catch (error) {
      logger.error('[VoteButton] Error casting vote', error instanceof Error ? error : new Error(String(error)));
      const message = error instanceof Error ? error.message : String(error);
      showError(message && message.length > 0 ? message : 'Unable to vote right now. Please try again.');
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, orientation === 'vertical' && styles.vertical]}>
        <ActivityIndicator size="small" color={theme.colors.textSecondary} />
      </View>
    );
  }

  if (!userId) {
    return null; // Don't show voting if user not logged in
  }

  const upvotes = aggregate?.upvotes || 0;
  const downvotes = aggregate?.downvotes || 0;
  const score = aggregate?.score || 0;

  const iconSize = size === 'small' ? 18 : size === 'large' ? 24 : 20;
  const buttonSize = size === 'small' ? 32 : size === 'large' ? 44 : 36;

  const upvoteColor = userVote === 'upvote' ? theme.colors.primary : theme.colors.textSecondary;
  const downvoteColor = userVote === 'downvote' ? theme.colors.error : theme.colors.textSecondary;

  return (
    <View style={[styles.container, orientation === 'vertical' && styles.vertical]}>
      {/* Upvote Button */}
      <Pressable
        onPress={() => handleVote('upvote')}
        disabled={voting}
        style={({ pressed }) => [
          styles.button,
          {
            width: buttonSize,
            height: buttonSize,
            backgroundColor: userVote === 'upvote' 
              ? theme.colors.primaryContainer 
              : theme.colors.surface,
            opacity: pressed ? 0.7 : 1,
          },
        ]}
      >
        <Ionicons name="chevron-up" size={iconSize} color={upvoteColor} />
        {showCounts && upvotes > 0 && (
          <Caption 
            style={[
              styles.count,
              { color: userVote === 'upvote' ? theme.colors.onPrimaryContainer : theme.colors.textSecondary },
            ]}
          >
            {upvotes}
          </Caption>
        )}
      </Pressable>

      {/* Score Display (optional) */}
      {showCounts && orientation === 'vertical' && (
        <ThemedText 
          style={[
            styles.score,
            { 
              color: score > 0 ? theme.colors.primary : score < 0 ? theme.colors.error : theme.colors.textSecondary,
              fontSize: size === 'small' ? 12 : size === 'large' ? 16 : 14,
            },
          ]}
        >
          {score > 0 ? '+' : ''}{score}
        </ThemedText>
      )}

      {/* Downvote Button */}
      <Pressable
        onPress={() => handleVote('downvote')}
        disabled={voting}
        style={({ pressed }) => [
          styles.button,
          {
            width: buttonSize,
            height: buttonSize,
            backgroundColor: userVote === 'downvote' 
              ? theme.colors.errorContainer 
              : theme.colors.surface,
            opacity: pressed ? 0.7 : 1,
          },
        ]}
      >
        <Ionicons name="chevron-down" size={iconSize} color={downvoteColor} />
        {showCounts && downvotes > 0 && (
          <Caption 
            style={[
              styles.count,
              { color: userVote === 'downvote' ? theme.colors.onErrorContainer : theme.colors.textSecondary },
            ]}
          >
            {downvotes}
          </Caption>
        )}
      </Pressable>

      {/* Score Display (horizontal) */}
      {showCounts && orientation === 'horizontal' && (
        <ThemedText 
          style={[
            styles.scoreHorizontal,
            { 
              color: score > 0 ? theme.colors.primary : score < 0 ? theme.colors.error : theme.colors.textSecondary,
              fontSize: size === 'small' ? 12 : size === 'large' ? 16 : 14,
            },
          ]}
        >
          {score > 0 ? '+' : ''}{score}
        </ThemedText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  vertical: {
    flexDirection: 'column',
    gap: 2,
  },
  button: {
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  count: {
    fontSize: 10,
    marginTop: -2,
    fontWeight: '600',
  },
  score: {
    fontWeight: '700',
    textAlign: 'center',
    marginVertical: 2,
  },
  scoreHorizontal: {
    fontWeight: '700',
    marginLeft: 4,
    minWidth: 30,
  },
});

